import { useEffect, useState } from "react";
import { LogOut, KeyRound, Mail, ShieldCheck } from "lucide-react";
import { api, apiErrorDetail } from "@/lib/api";

export default function AccountTab({ flash }) {
  const [me, setMe] = useState(null);
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/auth/me").then(({ data }) => setMe(data)).catch(() => setMe(null));
  }, []);

  const canSave =
    form.current.length >= 1 && form.next.length >= 8 && form.next === form.confirm;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/password", {
        current_password: form.current,
        new_password: form.next,
      });
      setForm({ current: "", next: "", confirm: "" });
      flash("Password aggiornata: le altre sessioni sono state disconnesse");
    } catch (err) {
      flash(apiErrorDetail(err), false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-8 max-w-2xl">
      <Section
        title="Account"
        description="Questo è l'account amministratore del pannello: chi accede con queste credenziali gestisce appuntamenti e sito."
      />
      <div className="bg-white rounded-[24px] shadow-soft p-6 flex flex-wrap items-center gap-4" data-testid="account-info">
        <span className="w-11 h-11 rounded-full bg-peach flex items-center justify-center shrink-0">
          <ShieldCheck size={20} className="text-deeptaupe" />
        </span>
        <span>
          <span className="block text-[11px] uppercase tracking-widest2 text-taupe font-medium">Email amministratore</span>
          <span className="block text-sm font-medium" data-testid="account-email">{me?.email || "—"}</span>
        </span>
        <button
          onClick={async () => {
            await api.post("/auth/logout").catch(() => {});
            window.location.href = "/admin";
          }}
          data-testid="account-logout"
          className="ml-auto inline-flex items-center gap-2 rounded-full border border-taupe/40 text-charcoal/70 text-[12px] uppercase tracking-widest2 font-medium px-6 py-2.5 hover:bg-peach transition-colors"
        >
          <LogOut size={14} /> Esci in sicurezza
        </button>
      </div>

      <div className="border-t border-taupe/20 pt-8">
        <Section
          title="Cambia password"
          description="Scegli una password di almeno 8 caratteri. Al salvataggio tutte le altre sessioni (altri dispositivi o browser) vengono disconnesse."
        />
        <form onSubmit={submit} className="bg-white rounded-[24px] shadow-soft p-6 space-y-5" data-testid="password-form">
          <Field
            label="Password attuale"
            value={form.current}
            onChange={(v) => setForm({ ...form, current: v })}
            testId="account-current-password"
            type="password"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Field
              label="Nuova password (min 8 caratteri)"
              value={form.next}
              onChange={(v) => setForm({ ...form, next: v })}
              testId="account-new-password"
              type="password"
            />
            <Field
              label="Conferma nuova password"
              value={form.confirm}
              onChange={(v) => setForm({ ...form, confirm: v })}
              testId="account-confirm-password"
              type="password"
              matchOk={form.confirm === "" || form.next === form.confirm}
            />
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <button
              type="submit"
              disabled={!canSave || busy}
              data-testid="account-password-save"
              className="inline-flex items-center gap-2 rounded-full bg-deeptaupe text-ivory text-[12px] uppercase tracking-widest2 font-medium px-8 py-3 hover:bg-charcoal transition-colors disabled:opacity-40"
            >
              <KeyRound size={14} /> {busy ? "Salvataggio…" : "Aggiorna password"}
            </button>
            {form.confirm !== "" && form.next !== form.confirm && (
              <span className="text-[13px] text-red-600" data-testid="password-mismatch">Le due password non coincidono</span>
            )}
          </div>
        </form>
      </div>

      <div className="border-t border-taupe/20 pt-8">
        <Section
          title="Email e recupero"
          description="L'email di recupero è quella dell'account, mostrata qui sopra. Se l'indirizzo cambia (per esempio passa alla casella dello studio), aggiornalo prima qui."
        />
        <div className="bg-peach/60 border border-taupe/25 rounded-2xl p-5 text-sm text-charcoal/80 flex gap-3" data-testid="account-recovery-note">
          <Mail size={16} className="shrink-0 mt-0.5 text-deeptaupe" />
          <p>
            Password dimenticata? Dalla schermata di accesso puoi richiedere un collegamento sicuro, valido 30
            minuti e utilizzabile una sola volta: viene inviato all'email dell'account indicata qui sopra.{" "}
            <a href="/admin/reset" data-testid="account-forgot-link" className="underline underline-offset-2 font-medium text-deeptaupe">
              Vai al recupero
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}

function Section({ title, description }) {
  return (
    <div className="mb-5">
      <h2 className="font-display text-2xl lg:text-3xl">{title}</h2>
      {description && <p className="text-sm text-charcoal/60 mt-2 max-w-2xl">{description}</p>}
    </div>
  );
}

function Field({ label, value, onChange, testId, type = "text", matchOk = true }) {
  return (
    <label className="block">
      <span className="block text-[12px] uppercase tracking-widest2 font-medium text-deeptaupe mb-2">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        autoComplete="new-password"
        className={`w-full rounded-2xl border bg-transparent px-4 py-3 text-sm focus:border-deeptaupe focus:outline-none ${matchOk ? "border-taupe/40" : "border-red-300"}`}
      />
    </label>
  );
}
